import PropTypes from "prop-types";

// React
import { memo } from "react";

// MUI
import { Grid, Stack, Typography } from "@mui/material";

// Components
import GradientHeader from "@Layouts/components/Basics/GradientHeader";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import RemoveCircleRounded from "@mui/icons-material/RemoveCircleRounded";

function ActionButtonContent(props) {
	return <SimpleIconButton {...props} />;
}

const ActionButton = memo(ActionButtonContent);

function RenderComponent(props) {
	const { component = null, title = "", titleClassName = "tiered-title" } = props;
	if (component) {
		return component;
	}

	return (
		<Typography variant="h5" component="div" className={titleClassName}>
			{title}
		</Typography>
	);
}
RenderComponent.propTypes = {
	component: PropTypes.node,
	title: PropTypes.string,
	titleClassName: PropTypes.string,
};
function HeaderContent(props) {
	const {
		title = "",
		titleClassName = "tiered-title",
		component = null,
		loading = false,
		showDivider = false,
		sxDivider = { marginLeft: "-17px" },
		actions = {
			disabledAdd: false,
			disabledRemove: false,
			onActionAdd: null,
			onActionRemove: null,
		},
	} = props;
	return (
		<GradientHeader showDivider={Boolean(showDivider)} sxDivider={sxDivider}>
			<Stack direction="row" justifyContent={"center"} alignContent={"center"} alignItems={"center"} spacing={1}>
				{Boolean(actions.onActionRemove) && (
					<Stack pb={0.5}>
						<ActionButton
							className={"btn-simple-remove !text-gray-blue"}
							ariaLabel="Remove item"
							disabled={actions.disabledRemove}
							loading={loading}
							loadingSize={20}
							sxLoading={{ padding: "2px" }}
							icon={<RemoveCircleOutlineRoundedIcon fontSize="medium" />}
							iconHover={<RemoveCircleRounded fontSize="medium" />}
							sx={{ padding: "3px" }}
							onClick={actions.onActionRemove}
						/>
					</Stack>
				)}
				<Stack>
					<RenderComponent component={component} title={title} titleClassName={titleClassName} />
				</Stack>
				{Boolean(actions.onActionAdd) && (
					<Stack pb={0.5}>
						<ActionButton
							className={"btn-simple-add !text-primary"}
							ariaLabel="Add item"
							disabled={actions.disabledAdd}
							loading={loading}
							loadingSize={20}
							sxLoading={{ padding: "2px" }}
							icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
							iconHover={<AddCircleRounded fontSize="medium" />}
							sx={{ padding: "3px" }}
							onClick={actions.onActionAdd}
						/>
					</Stack>
				)}
			</Stack>
		</GradientHeader>
	);
}
HeaderContent.propTypes = {
	title: PropTypes.string,
	titleClassName: PropTypes.string,
	component: PropTypes.node,
	loading: PropTypes.bool,
	showDivider: PropTypes.bool,
	sxDivider: PropTypes.object,
	actions: PropTypes.shape({
		onActionAdd: PropTypes.func,
		disabledAdd: PropTypes.bool,
		onActionRemove: PropTypes.func,
		disabledRemove: PropTypes.bool,
	}),
};

const Header = memo(HeaderContent);

export default function TierExpandableHeader(props) {
	const {
		headers = [],
		containerProps = {
			direction: "row",
			justifyContent: "flex-start",
			columnSpacing: 4,
		},
	} = props;

	return (
		<Grid container {...containerProps}>
			{headers.map((header, key) => {
				const itemProps = header.itemProps || { sm: 12, md: 2 };
				return (
					<Grid key={`${header?.title}-${key}`} item className="simple-transition" {...itemProps} style={header.style || null}>
						<Header {...header} />
					</Grid>
				);
			})}
		</Grid>
	);
}
TierExpandableHeader.propTypes = {
	headers: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			titleClassName: PropTypes.string,
			component: PropTypes.node,
			loading: PropTypes.bool,
			showDivider: PropTypes.bool,
			sxDivider: PropTypes.object,
			actions: PropTypes.shape({
				onActionAdd: PropTypes.func,
				disabledAdd: PropTypes.bool,
				onActionRemove: PropTypes.func,
				disabledRemove: PropTypes.bool,
			}),
			itemProps: PropTypes.object,
			style: PropTypes.object,
		})
	),
	containerProps: PropTypes.object,
};
